<script lang="ts" setup>
import type { ChartData, ChartOptions, TooltipOptions } from 'chart.js'
import type { MetricChartData, MetricChartDataset, MetricOptions } from '@/types/charts.types'
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  TimeScale,
  Tooltip,
} from 'chart.js'
import { Bar } from 'vue-chartjs'
import 'chartjs-adapter-date-fns'

interface Props {
  data: MetricChartData<'bar'>
  options: MetricOptions<'bar'>
}

const props = defineProps<Props>()

ChartJS.register(
  Tooltip,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  TimeScale,
)

const { data, options } = toRefs(props)
const chartData = shallowRef<ChartData<'bar'>>(data.value as ChartData<'bar'>)
const chartOptions = shallowRef<ChartOptions<'bar'>>(options.value)

const filteredDatasets = computed<MetricChartDataset<'bar'>[]>(() => data.value?.datasets?.filter(d => d.data?.length && d.data.length > 0) || [])

const extendedOptions = reactive(useChartExtendedOptions<'bar'>(options, filteredDatasets))
const tooltip = reactive(useChartTooltip(options, filteredDatasets))
const renderIndex = ref(0)

watch(
  [data, options, extendedOptions, tooltip],
  ([data, options, extendedOptions, tooltip]) => {
    const dataRaw = markRaw(clone(data))

    const newOptions = {
      ...DEFAULT_CHART_OPTIONS,
      ...options,
    } as MetricOptions<'bar'>

    if (extendedOptions.showStatus && extendedOptions.statusSeries) {
      dataRaw.datasets?.push(extendedOptions.statusSeries)
    } else if (extendedOptions.showWeekend) {
      dataRaw.datasets?.push(extendedOptions.weekendSeries)
    }

    if (dataRaw.datasets.length === 0) {
      dataRaw.datasets.push({})
    }

    if (newOptions.scales) {
      newOptions.scales = extendedOptions.getExtendedOptions(newOptions.scales)
    }

    newOptions.plugins = {
      ...newOptions.plugins,
      // TODO: put useChartTooltip in typescript to avoid this
      tooltip: tooltip as unknown as TooltipOptions<'bar'>,
    }

    chartData.value = dataRaw as ChartData<'bar'>
    chartOptions.value = newOptions
    renderIndex.value++
  },
  { immediate: true, deep: true },
)
</script>

<template>
  <Bar
    :key="renderIndex"
    :data="chartData"
    :options="chartOptions"
    style="position: relative; height: 200px"
  />
</template>
